/**
 * Image Loading Strategy Utilities
 * 
 * These utilities help determine the optimal image loading strategy
 * based on various factors like position, importance, and context.
 */

export type LoadingStrategy = 'eager' | 'lazy' | 'intersection';

export interface ImageLoadingConfig {
  strategy: LoadingStrategy;
  threshold?: number;
  rootMargin?: string;
  priority?: 'high' | 'medium' | 'low';
}

export interface ViewportDimensions {
  width: number;
  height: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

export interface GridLayout {
  columns: number;
  gap: number;
  aspectRatio: number;
  padding?: number;
}

/**
 * Calculates the optimal eager loading threshold based on viewport size
 * and actual grid layout.
 */
export function calculateEagerThreshold(
  viewport: ViewportDimensions,
  gridLayout: GridLayout,
  options: {
    buffer?: number; // Extra images to load for smooth scrolling
    maxThreshold?: number; // Maximum number of images to load eagerly
    considerHeaderHeight?: number; // Account for fixed headers/navigation
  } = {}
): number {
  const {
    buffer = 2,
    maxThreshold = 25,
    considerHeaderHeight = 0,
  } = options;

  const { width, height } = viewport;
  const { columns, gap, aspectRatio, padding = 0 } = gridLayout;

  // Calculate available space for images
  const availableWidth = width - (padding * 2);
  const availableHeight = height - considerHeaderHeight - (padding * 2);

  // Calculate image dimensions
  const imageWidth = (availableWidth - (gap * (columns - 1))) / columns;
  const imageHeight = imageWidth / aspectRatio;

  // Calculate how many images fit in the viewport
  const imagesPerRow = Math.min(columns, Math.floor(availableWidth / (imageWidth + gap)));
  const rowsInViewport = Math.floor(availableHeight / (imageHeight + gap));

  // Calculate total images that fit in viewport
  const imagesInViewport = imagesPerRow * rowsInViewport;

  // Add buffer for smooth scrolling experience
  const threshold = Math.min(imagesInViewport + buffer, maxThreshold);

  return Math.max(threshold, 1); // At least load 1 image eagerly
}

/**
 * Determines if an image should be considered "above the fold"
 * based on its position and the actual viewport size.
 */
export function isAboveTheFold(
  index: number,
  viewport: ViewportDimensions,
  gridLayout: GridLayout,
  options: {
    considerHeaderHeight?: number;
    padding?: number;
  } = {}
): boolean {
  const { considerHeaderHeight = 0, padding = 0 } = options;
  const { width, height } = viewport;
  const { columns, gap, aspectRatio } = gridLayout;

  // Calculate available space
  const availableWidth = width - (padding * 2);
  const availableHeight = height - considerHeaderHeight - (padding * 2);

  // Calculate image dimensions
  const imageWidth = (availableWidth - (gap * (columns - 1))) / columns;
  const imageHeight = imageWidth / aspectRatio;

  // Calculate how many images fit in the viewport
  const imagesPerRow = Math.min(columns, Math.floor(availableWidth / (imageWidth + gap)));
  const rowsInViewport = Math.floor(availableHeight / (imageHeight + gap));

  return index < (imagesPerRow * rowsInViewport);
}

/**
 * Determines the optimal loading strategy for an image based on its position
 * and importance in the layout.
 */
export function getImageLoadingStrategy(
  index: number,
  totalImages: number,
  viewport: ViewportDimensions,
  gridLayout: GridLayout,
  options: {
    isHero?: boolean;
    isCritical?: boolean;
    considerHeaderHeight?: number;
    buffer?: number;
    maxThreshold?: number;
  } = {}
): ImageLoadingConfig {
  const {
    isHero = false,
    isCritical = false,
    considerHeaderHeight = 0,
    buffer = 2,
    maxThreshold = 25,
  } = options;

  // Hero images and critical content always load eagerly
  if (isHero || isCritical) {
    return {
      strategy: 'eager',
      priority: 'high',
    };
  }

  // Calculate eager threshold based on viewport and grid
  const eagerThreshold = calculateEagerThreshold(viewport, gridLayout, {
    buffer,
    maxThreshold,
    considerHeaderHeight,
  });

  // Check if image is above the fold
  const isAboveTheFoldContent = isAboveTheFold(index, viewport, gridLayout, {
    considerHeaderHeight,
  });

  // Above-the-fold images load eagerly
  if (isAboveTheFoldContent) {
    return {
      strategy: 'eager',
      priority: 'high',
    };
  }

  // First N images load eagerly (above-the-fold approximation)
  if (index < eagerThreshold) {
    return {
      strategy: 'eager',
      priority: 'medium',
    };
  }

  // Rest use intersection observer for better control
  return {
    strategy: 'intersection',
    threshold: 0.1,
    rootMargin: '100px',
    priority: 'low',
  };
}

/**
 * Creates a loading configuration for a list of images
 * with intelligent prioritization based on viewport.
 */
export function createImageLoadingConfigs(
  totalImages: number,
  viewport: ViewportDimensions,
  gridLayout: GridLayout,
  options: {
    heroIndices?: number[];
    criticalIndices?: number[];
    considerHeaderHeight?: number;
    buffer?: number;
    maxThreshold?: number;
  } = {}
): ImageLoadingConfig[] {
  const {
    heroIndices = [],
    criticalIndices = [],
    considerHeaderHeight = 0,
    buffer = 2,
    maxThreshold = 25,
  } = options;

  return Array.from({ length: totalImages }, (_, index) => {
    const isHero = heroIndices.includes(index);
    const isCritical = criticalIndices.includes(index);

    return getImageLoadingStrategy(index, totalImages, viewport, gridLayout, {
      isHero,
      isCritical,
      considerHeaderHeight,
      buffer,
      maxThreshold,
    });
  });
}

/**
 * Gets grid layout configuration based on viewport size
 * and component type.
 */
export function getGridLayout(
  viewport: ViewportDimensions,
  componentType: 'places' | 'spots' | 'photos' | 'custom'
): GridLayout {
  switch (componentType) {
    case 'places':
      if (viewport.isMobile) {
        return { columns: 2, gap: 8, aspectRatio: 177/142 };
      } else if (viewport.isTablet) {
        return { columns: 3, gap: 12, aspectRatio: 177/142 };
      } else {
        return { columns: 2, gap: 12, aspectRatio: 177/142 };
      }
    
    case 'spots':
      return { columns: 3, gap: 8, aspectRatio: 115/169 };
    
    case 'photos':
      return { columns: 1, gap: 8, aspectRatio: 11/13 };
    
    default:
      return { columns: 4, gap: 16, aspectRatio: 1 };
  }
}

/**
 * Performance monitoring utilities for image loading
 */
export class ImageLoadingMetrics {
  private static instance: ImageLoadingMetrics;
  private metrics: Map<string, {
    startTime: number;
    loadTime?: number;
    strategy: LoadingStrategy;
    success: boolean;
    viewport: ViewportDimensions;
  }> = new Map();

  static getInstance(): ImageLoadingMetrics {
    if (!ImageLoadingMetrics.instance) {
      ImageLoadingMetrics.instance = new ImageLoadingMetrics();
    }
    return ImageLoadingMetrics.instance;
  }

  startLoading(imageId: string, strategy: LoadingStrategy, viewport: ViewportDimensions): void {
    this.metrics.set(imageId, {
      startTime: performance.now(),
      strategy,
      success: false,
      viewport,
    });
  }

  finishLoading(imageId: string, success: boolean = true): void {
    const metric = this.metrics.get(imageId);
    if (metric) {
      metric.loadTime = performance.now() - metric.startTime;
      metric.success = success;
    }
  }

  getMetrics(): Array<{
    imageId: string;
    loadTime: number;
    strategy: LoadingStrategy;
    success: boolean;
    viewport: ViewportDimensions;
  }> {
    return Array.from(this.metrics.entries())
      .filter(([_, metric]) => metric.loadTime !== undefined)
      .map(([imageId, metric]) => ({
        imageId,
        loadTime: metric.loadTime!,
        strategy: metric.strategy,
        success: metric.success,
        viewport: metric.viewport,
      }));
  }

  getAverageLoadTime(): number {
    const metrics = this.getMetrics();
    if (metrics.length === 0) return 0;
    
    const totalTime = metrics.reduce((sum, metric) => sum + metric.loadTime, 0);
    return totalTime / metrics.length;
  }

  getStrategyPerformance(): Record<LoadingStrategy, number> {
    const metrics = this.getMetrics();
    const strategyTimes: Record<LoadingStrategy, number[]> = {
      eager: [],
      lazy: [],
      intersection: [],
    };

    metrics.forEach(metric => {
      strategyTimes[metric.strategy].push(metric.loadTime);
    });

    return {
      eager: strategyTimes.eager.length > 0 
        ? strategyTimes.eager.reduce((a, b) => a + b, 0) / strategyTimes.eager.length 
        : 0,
      lazy: strategyTimes.lazy.length > 0 
        ? strategyTimes.lazy.reduce((a, b) => a + b, 0) / strategyTimes.lazy.length 
        : 0,
      intersection: strategyTimes.intersection.length > 0 
        ? strategyTimes.intersection.reduce((a, b) => a + b, 0) / strategyTimes.intersection.length 
        : 0,
    };
  }

  getViewportPerformance(): Record<string, number> {
    const metrics = this.getMetrics();
    const viewportTimes: Record<string, number[]> = {};

    metrics.forEach(metric => {
      const viewportKey = `${metric.viewport.width}x${metric.viewport.height}`;
      if (!viewportTimes[viewportKey]) {
        viewportTimes[viewportKey] = [];
      }
      viewportTimes[viewportKey].push(metric.loadTime);
    });

    const result: Record<string, number> = {};
    Object.entries(viewportTimes).forEach(([viewport, times]) => {
      result[viewport] = times.reduce((a, b) => a + b, 0) / times.length;
    });

    return result;
  }
} 

/**
 * Debug utility for tracking image loading issues
 */
export class ImageLoadingDebugger {
  private static instance: ImageLoadingDebugger;
  private logs: Array<{
    timestamp: number;
    type: 'load' | 'error' | 'retry' | 'fallback';
    src: string;
    message: string;
    viewport?: ViewportDimensions;
  }> = [];

  static getInstance(): ImageLoadingDebugger {
    if (!ImageLoadingDebugger.instance) {
      ImageLoadingDebugger.instance = new ImageLoadingDebugger();
    }
    return ImageLoadingDebugger.instance;
  }

  log(type: 'load' | 'error' | 'retry' | 'fallback', src: string, message: string, viewport?: ViewportDimensions): void {
    this.logs.push({
      timestamp: Date.now(),
      type,
      src,
      message,
      viewport,
    });

    // Keep only last 100 logs
    if (this.logs.length > 100) {
      this.logs = this.logs.slice(-100);
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[ImageLoading] ${type.toUpperCase()}: ${message}`, { src, viewport });
    }
  }

  getLogs(): typeof this.logs {
    return [...this.logs];
  }

  getErrors(): typeof this.logs {
    return this.logs.filter(log => log.type === 'error');
  }

  getRetries(): typeof this.logs {
    return this.logs.filter(log => log.type === 'retry');
  }

  clearLogs(): void {
    this.logs = [];
  }

  getStats(): {
    total: number;
    loads: number;
    errors: number;
    retries: number;
    fallbacks: number;
    errorRate: number;
  } {
    const total = this.logs.length;
    const loads = this.logs.filter(log => log.type === 'load').length;
    const errors = this.logs.filter(log => log.type === 'error').length;
    const retries = this.logs.filter(log => log.type === 'retry').length;
    const fallbacks = this.logs.filter(log => log.type === 'fallback').length;

    return {
      total,
      loads,
      errors,
      retries,
      fallbacks,
      errorRate: total > 0 ? (errors / total) * 100 : 0,
    };
  }
}

/**
 * Utility function to validate image URLs
 */
export function isValidImageUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const urlObj = new URL(url);
    return ['http:', 'https:'].includes(urlObj.protocol);
  } catch {
    // If URL parsing fails, it might be a relative path
    return url.startsWith('/') || url.startsWith('./') || url.startsWith('../');
  }
}

/**
 * Utility function to get image dimensions from URL
 */
export function getImageDimensionsFromUrl(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Only add crossOrigin if explicitly needed (causes CORS issues with some CDNs)
    // img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    
    img.onerror = () => {
      reject(new Error(`Failed to load image: ${url}`));
    };
    
    img.src = url;
  });
}

/**
 * Utility function to preload critical images
 */
export function preloadCriticalImages(urls: string[]): Promise<void[]> {
  const promises = urls.map(url => {
    return new Promise<void>((resolve, reject) => {
      const img = new Image();
      // Only add crossOrigin if explicitly needed (causes CORS issues with some CDNs)
      // img.crossOrigin = 'anonymous';
      
      img.onload = () => resolve();
      img.onerror = () => reject(new Error(`Failed to preload: ${url}`));
      
      img.src = url;
    });
  });

  return Promise.all(promises);
} 